import {
  assertImportFileSize,
  BoqImportError,
  detectImportFormat,
  type ImportFileInput,
  type ImportProgress,
  type ImportSourceType,
  type ImportedWorkbookMetadata,
  type ParsedSheet,
  type ParseSheetOptions,
} from './import/types'
import { mapSheetToBoqRows, type MappedBoqSheet } from './mapSheetToBoqRows'
import type { ColumnMapping } from '../types'
import type {
  BoqWorkerRequest,
  BoqWorkerResponse,
  WorkerOperation,
  WorkerOperationResultMap,
} from '../workers/protocol'

export interface WorkerClientRequestOptions {
  onProgress?: (progress: ImportProgress) => void
}

interface PendingRequest {
  operation: WorkerOperation
  resolve(value: unknown): void
  reject(reason: unknown): void
  onProgress?: (progress: ImportProgress) => void
}

function createDefaultWorker(): Worker {
  if (typeof Worker === 'undefined') {
    throw new BoqImportError({
      code: 'WORKER_UNAVAILABLE',
      message: '当前浏览器不支持后台解析线程，请更换现代浏览器后重试。',
    })
  }
  return new Worker(new URL('../workers/boqParser.worker.ts', import.meta.url), {
    type: 'module',
    name: 'boq-diff-parser',
  })
}

/**
 * Typed, single-purpose client for the local parser worker. File bytes are
 * transferred directly into the worker and are never uploaded or persisted.
 */
export class BoqParseWorkerClient {
  private readonly worker: Worker
  private readonly pending = new Map<string, PendingRequest>()
  private sequence = 0
  private disposed = false

  constructor(worker: Worker = createDefaultWorker()) {
    this.worker = worker
    this.worker.addEventListener('message', this.handleMessage)
    this.worker.addEventListener('error', this.handleWorkerError)
    this.worker.addEventListener('messageerror', this.handleWorkerError)
  }

  async loadFile(
    file: ImportFileInput,
    sourceType: ImportSourceType,
    options: WorkerClientRequestOptions = {},
  ): Promise<ImportedWorkbookMetadata> {
    this.assertActive()
    const format = detectImportFormat(file.name)
    assertImportFileSize(file.name, file.size)

    const requestId = this.nextRequestId('load')
    options.onProgress?.({
      requestId,
      stage: 'reading-file',
      progress: 0,
      message: '正在将文件读入浏览器内存…',
    })
    const bytes = await file.arrayBuffer()
    assertImportFileSize(file.name, bytes.byteLength)
    options.onProgress?.({
      requestId,
      stage: 'reading-file',
      progress: 1,
      message: '文件读取完成，准备解析…',
    })

    const workbookId = `${sourceType}-${requestId}`
    const request: BoqWorkerRequest = {
      kind: 'load-workbook',
      requestId,
      workbookId,
      sourceType,
      file: {
        name: file.name,
        size: file.size,
        type: file.type ?? '',
        lastModified: file.lastModified ?? 0,
        format,
        bytes,
      },
    }
    return this.send(request, 'load-workbook', options, [bytes])
  }

  parseSheet(
    workbookId: string,
    sheetName: string,
    options: ParseSheetOptions,
    requestOptions: WorkerClientRequestOptions = {},
  ): Promise<ParsedSheet> {
    const request: BoqWorkerRequest = {
      kind: 'parse-sheet',
      requestId: this.nextRequestId('sheet'),
      workbookId,
      sheetName,
      options,
    }
    return this.send(request, 'parse-sheet', requestOptions)
  }

  async parseMappedSheet(
    workbookId: string,
    sheetName: string,
    mapping: ColumnMapping,
    requestOptions: WorkerClientRequestOptions = {},
  ): Promise<MappedBoqSheet> {
    if (mapping.headerRow === null) {
      throw new BoqImportError({
        code: 'INVALID_FILE',
        message: '请先选择标题行并完成字段映射。',
      })
    }
    const parsed = await this.parseSheet(
      workbookId,
      sheetName,
      { headerRow: mapping.headerRow },
      requestOptions,
    )
    return mapSheetToBoqRows(parsed, mapping)
  }

  async releaseWorkbook(workbookId: string): Promise<boolean> {
    const request: BoqWorkerRequest = {
      kind: 'release-workbook',
      requestId: this.nextRequestId('release'),
      workbookId,
    }
    const result = await this.send(request, 'release-workbook')
    return result.released
  }

  async clear(): Promise<void> {
    const request: BoqWorkerRequest = {
      kind: 'clear-workbooks',
      requestId: this.nextRequestId('clear'),
    }
    await this.send(request, 'clear-workbooks')
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.worker.removeEventListener('message', this.handleMessage)
    this.worker.removeEventListener('error', this.handleWorkerError)
    this.worker.removeEventListener('messageerror', this.handleWorkerError)
    this.worker.terminate()
    const cancellation = new BoqImportError({
      code: 'REQUEST_CANCELLED',
      message: '文件解析已取消。',
    })
    for (const pending of this.pending.values()) pending.reject(cancellation)
    this.pending.clear()
  }

  private nextRequestId(prefix: string): string {
    this.sequence += 1
    return `${prefix}-${String(this.sequence)}`
  }

  private assertActive(): void {
    if (!this.disposed) return
    throw new BoqImportError({
      code: 'REQUEST_CANCELLED',
      message: '文件解析器已关闭，请重新开始。',
    })
  }

  private send<T extends WorkerOperation>(
    request: BoqWorkerRequest,
    operation: T,
    options: WorkerClientRequestOptions = {},
    transfer: Transferable[] = [],
  ): Promise<WorkerOperationResultMap[T]> {
    this.assertActive()
    return new Promise<WorkerOperationResultMap[T]>((resolve, reject) => {
      this.pending.set(request.requestId, {
        operation,
        resolve,
        reject,
        ...(options.onProgress ? { onProgress: options.onProgress } : {}),
      })
      this.worker.postMessage(request, transfer)
    })
  }

  private readonly handleMessage = (event: MessageEvent<BoqWorkerResponse>): void => {
    const response = event.data
    const pending = this.pending.get(response.requestId)
    if (!pending) return

    if (response.kind === 'progress') {
      pending.onProgress?.(response.data)
      return
    }

    this.pending.delete(response.requestId)
    if (response.kind === 'failure') {
      pending.reject(new BoqImportError(response.error))
      return
    }

    if (response.operation !== pending.operation) {
      pending.reject(
        new BoqImportError({
          code: 'WORKER_FAILURE',
          message: '解析线程返回了不一致的结果，请重试。',
        }),
      )
      return
    }
    pending.resolve(response.data)
  }

  private readonly handleWorkerError = (): void => {
    const failure = new BoqImportError({
      code: 'WORKER_FAILURE',
      message: '文件解析线程异常终止，请重新选择文件。',
    })
    for (const pending of this.pending.values()) pending.reject(failure)
    this.pending.clear()
  }
}

export function createBoqParseWorkerClient(): BoqParseWorkerClient {
  return new BoqParseWorkerClient()
}
